/**
 * BULK OPERATIVO DEL PLANNER — FASE 6.7-B1
 *
 *     Seleccionar OTs → acción → configurar → PREVIEW → ver resultado por
 *     OT y avisos → Confirmar → ejecutar → refetch.
 *
 * UI != autoridad: nada de lo que aquí se muestra autoriza nada. El preview
 * (`POST .../bulk/preview`) sólo informa; la ejecución (`.../bulk/reprogramar`,
 * `.../bulk/asignaciones`) revalida todo en el backend, dentro de una única
 * transacción TODO-O-NADA. Este archivo nunca calcula elegibilidad, fechas
 * ni avisos: sólo presenta lo que devuelve el backend.
 *
 * Nunca se ejecuta desde el primer formulario: Confirmar sólo existe tras un
 * preview válido, y cualquier cambio de configuración o de selección lo
 * invalida. Con alguna OT NO_ELEGIBLE, Confirmar queda deshabilitado.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import {
  api,
  type AlertaPlanificacion,
  type FilaPreviewBulk,
  type OperacionBulkPlanificacion,
  type OperarioAsignadoEvento,
  type ResultadoBulkAsignaciones,
  type ResultadoBulkReprogramar,
  type ResultadoPreviewBulk,
} from '../api.ts';
import { Error as ErrorCaja, etiquetaEstadoOt, Modal, useDatos } from './base.tsx';

/** Espejo del límite del backend (`MAX_OTS_BULK_PLANIFICACION`) — sólo para avisar antes; el backend lo exige igual. */
export const MAX_OTS_BULK = 100;
/** Mismo snap que `PLANNER_INTERVALO_MINUTOS` (planner.tsx). */
const INTERVALO_MINUTOS = 15;

const TITULOS: Record<OperacionBulkPlanificacion, string> = {
  DESPLAZAR: 'Desplazar fecha/hora',
  ANADIR_OPERARIO: 'Añadir operario',
  QUITAR_OPERARIO: 'Quitar operario',
};

// ------------------------------------------------------------- barra de selección

export function BarraBulk({
  seleccionadas, puedeReprogramar, puedeAsignar, abrir, limpiar,
}: {
  seleccionadas: number;
  puedeReprogramar: boolean;
  puedeAsignar: boolean;
  abrir: (op: OperacionBulkPlanificacion) => void;
  limpiar: () => void;
}): ReactNode {
  const excede = seleccionadas > MAX_OTS_BULK;
  const sinNada = seleccionadas === 0;
  return (
    <div className="planner-barra-bulk" role="region" aria-label="Acciones en lote">
      <span className="planner-barra-bulk-contador" aria-live="polite">
        {seleccionadas === 1 ? '1 OT seleccionada' : `${String(seleccionadas)} OTs seleccionadas`}
      </span>
      {excede && (
        <span className="pequena" role="status">
          ⚠ Como máximo {MAX_OTS_BULK} OTs por operación.
        </span>
      )}
      <span className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
        {puedeReprogramar && (
          <button type="button" className="secundario" disabled={sinNada || excede} onClick={() => abrir('DESPLAZAR')}>Desplazar</button>
        )}
        {puedeAsignar && (
          <>
            <button type="button" className="secundario" disabled={sinNada || excede} onClick={() => abrir('ANADIR_OPERARIO')}>Añadir operario</button>
            <button type="button" className="secundario" disabled={sinNada || excede} onClick={() => abrir('QUITAR_OPERARIO')}>Quitar operario</button>
          </>
        )}
        <button type="button" className="enlace" disabled={sinNada} onClick={limpiar}>Limpiar selección</button>
      </span>
    </div>
  );
}

// ------------------------------------------------------------- diálogo

function entero(v: string): number {
  return v.trim() === '' ? 0 : Number(v);
}

function textoResultado(f: FilaPreviewBulk, operacion: OperacionBulkPlanificacion): string {
  if (f.resultado === 'NO_ELEGIBLE') return 'No elegible';
  if (f.resultado === 'SIN_CAMBIO') return 'Sin cambio';
  return operacion === 'DESPLAZAR' ? 'Se moverá' : operacion === 'ANADIR_OPERARIO' ? 'Se asignará' : 'Se quitará';
}
function claseResultado(f: FilaPreviewBulk): string {
  return f.resultado === 'NO_ELEGIBLE' ? 'error' : f.resultado === 'SIN_CAMBIO' ? 'neutra' : 'exito';
}
function formatoLocal(local: string | null): string {
  return local === null ? 'sin hora' : `${local.slice(0, 10)} ${local.slice(11, 16)}`;
}

export function DialogoBulk({
  operacion, idCentro, idsOt, operariosDeSeleccion, quitarDeSeleccion, cerrar, alTerminar,
}: {
  operacion: OperacionBulkPlanificacion;
  idCentro: string;
  idsOt: readonly string[];
  /** Para "Quitar operario": sólo tiene sentido ofrecer a quien está asignado en alguna OT seleccionada. */
  operariosDeSeleccion: readonly OperarioAsignadoEvento[];
  quitarDeSeleccion: (idOt: string) => void;
  cerrar: () => void;
  alTerminar: (mensaje: string) => void;
}): ReactNode {
  const [dias, setDias] = useState('0');
  const [horas, setHoras] = useState('0');
  const [minutos, setMinutos] = useState('0');
  const [motivo, setMotivo] = useState('');
  const [idOperario, setIdOperario] = useState('');
  const [preview, setPreview] = useState<ResultadoPreviewBulk | null>(null);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  //  Doble envío: el estado de React no es síncrono; el ref sí.
  const enCursoRef = useRef(false);
  const tituloPreviewRef = useRef<HTMLHeadingElement>(null);

  const operariosCentro = useDatos(
    () => (operacion === 'ANADIR_OPERARIO'
      ? api.get<{ operarios: { id: string; codigo: string; nombre: string; apellidos: string }[] }>(`/api/operarios?idCentro=${idCentro}&activo=true&dominio=LIMPIEZA`)
      : Promise.resolve({ operarios: [] })),
    [operacion, idCentro],
  );
  const opcionesOperario: { id: string; texto: string }[] = operacion === 'ANADIR_OPERARIO'
    ? (operariosCentro.datos?.operarios ?? []).map((o) => ({ id: o.id, texto: `${o.nombre} ${o.apellidos} (${o.codigo})` }))
    : operariosDeSeleccion.map((o) => ({ id: o.idOperario, texto: `${o.nombre} (${o.codigo})` }));

  //  Cualquier cambio de la selección invalida el preview ya visto.
  const claveSeleccion = idsOt.join(',');
  useEffect(() => { setPreview(null); }, [claveSeleccion]);
  //  Foco al título del resultado: el lector de pantalla anuncia que hay preview.
  useEffect(() => { if (preview !== null) tituloPreviewRef.current?.focus(); }, [preview]);

  function invalidar<T>(set: (v: T) => void): (v: T) => void {
    return (v) => { set(v); setPreview(null); setError(null); };
  }

  const delta = { dias: entero(dias), horas: entero(horas), minutos: entero(minutos) };
  function errorDeForma(): string | null {
    if (idsOt.length === 0) return 'No hay ninguna OT seleccionada.';
    if (operacion === 'DESPLAZAR') {
      if (![delta.dias, delta.horas, delta.minutos].every(Number.isInteger)) return 'Días, horas y minutos tienen que ser números enteros.';
      if (delta.minutos % INTERVALO_MINUTOS !== 0) return `Los minutos tienen que ser múltiplo de ${String(INTERVALO_MINUTOS)}.`;
      if (delta.dias * 1440 + delta.horas * 60 + delta.minutos === 0) return 'Indica un desplazamiento distinto de cero.';
      return null;
    }
    return idOperario === '' ? 'Elige un operario.' : null;
  }

  async function previsualizar(): Promise<void> {
    const forma = errorDeForma();
    if (forma !== null) { setError(new globalThis.Error(forma)); return; }
    setError(null);
    setCargando(true);
    try {
      const cuerpo = operacion === 'DESPLAZAR'
        ? { operacion, idsOt, delta }
        : { operacion, idsOt, idOperario };
      setPreview(await api.post<ResultadoPreviewBulk>(`/api/centros/${idCentro}/planificacion/bulk/preview`, cuerpo));
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (preview === null || !preview.puedeConfirmar || enCursoRef.current) return;
    enCursoRef.current = true;
    setEnviando(true);
    setError(null);
    try {
      if (operacion === 'DESPLAZAR') {
        const r = await api.post<ResultadoBulkReprogramar>(`/api/centros/${idCentro}/planificacion/bulk/reprogramar`, {
          ots: preview.filas.map((f) => ({ idOt: f.idOt, inicioProgramadoEn: f.inicioActual })),
          delta,
          motivo: motivo.trim() === '' ? undefined : motivo.trim(),
        });
        alTerminar(r.reprogramadas === 1 ? '1 OT reprogramada.' : `${String(r.reprogramadas)} OTs reprogramadas.`);
      } else {
        const r = await api.post<ResultadoBulkAsignaciones>(`/api/centros/${idCentro}/planificacion/bulk/asignaciones`, {
          idsOt, operacion: operacion === 'ANADIR_OPERARIO' ? 'ANADIR' : 'RETIRAR', idOperario,
        });
        const verbo = operacion === 'ANADIR_OPERARIO' ? 'Operario añadido' : 'Operario quitado';
        alTerminar(`${verbo} en ${String(r.cambiadas)} OT(s)${r.sinCambio > 0 ? `; ${String(r.sinCambio)} sin cambio` : ''}.`);
      }
    } catch (e) {
      //  Nada se aplicó (todo-o-nada). El preview visto ya no vale.
      setError(e);
      setPreview(null);
    } finally {
      enCursoRef.current = false;
      setEnviando(false);
    }
  }

  const bloqueado = cargando || enviando;
  return (
    <Modal titulo={`${TITULOS[operacion]} · ${idsOt.length === 1 ? '1 OT' : `${String(idsOt.length)} OTs`}`} cerrar={() => { if (!enviando) cerrar(); }}>
      {error !== null && <ErrorCaja error={error} />}

      <fieldset disabled={bloqueado} style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="sr-solo">Configuración</legend>
        {operacion === 'DESPLAZAR' ? (
          <>
            <p className="pequena suave">
              Cada OT se desplaza lo mismo respecto a su propio inicio — no se ponen todas a la misma hora.
              Los días se cuentan en el calendario local del centro (se mantiene la hora local aunque cambie la hora oficial).
            </p>
            <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="campo">
                <label htmlFor="bulk-dias">Días</label>
                <input id="bulk-dias" type="number" step={1} value={dias} onChange={(e) => invalidar(setDias)(e.target.value)} />
              </div>
              <div className="campo">
                <label htmlFor="bulk-horas">Horas</label>
                <input id="bulk-horas" type="number" step={1} value={horas} onChange={(e) => invalidar(setHoras)(e.target.value)} />
              </div>
              <div className="campo">
                <label htmlFor="bulk-minutos">Minutos (múltiplo de {INTERVALO_MINUTOS})</label>
                <input id="bulk-minutos" type="number" step={INTERVALO_MINUTOS} value={minutos} onChange={(e) => invalidar(setMinutos)(e.target.value)} />
              </div>
            </div>
            <div className="campo">
              <label htmlFor="bulk-motivo">Motivo (opcional)</label>
              <input id="bulk-motivo" type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </div>
          </>
        ) : (
          <div className="campo">
            <label htmlFor="bulk-operario">Operario</label>
            <select id="bulk-operario" value={idOperario} onChange={(e) => invalidar(setIdOperario)(e.target.value)}>
              <option value="">Elige un operario…</option>
              {opcionesOperario.map((o) => <option key={o.id} value={o.id}>{o.texto}</option>)}
            </select>
            {operacion === 'QUITAR_OPERARIO' && opcionesOperario.length === 0 && (
              <p className="pequena suave">Ninguna de las OTs seleccionadas tiene operarios asignados.</p>
            )}
            <p className="pequena suave">Se aplica a toda la OT (no por líneas).</p>
          </div>
        )}
        <div className="fila" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className={preview === null ? 'principal' : 'secundario'} onClick={() => void previsualizar()}>
            {cargando ? 'Calculando…' : preview === null ? 'Previsualizar' : 'Volver a previsualizar'}
          </button>
        </div>
      </fieldset>

      {preview !== null && (
        <PreviewBulk
          preview={preview} operacion={operacion} tituloRef={tituloPreviewRef}
          quitarDeSeleccion={quitarDeSeleccion} bloqueado={bloqueado}
        />
      )}

      {preview !== null && (
        <div className="fila" style={{ gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="secundario" onClick={cerrar} disabled={enviando}>Cancelar</button>
          <button type="button" className="principal" onClick={() => void confirmar()} disabled={!preview.puedeConfirmar || bloqueado}>
            {enviando ? 'Aplicando…' : 'Confirmar'}
          </button>
        </div>
      )}
    </Modal>
  );
}

function ListaAvisos({ alertas }: { alertas: readonly AlertaPlanificacion[] }): ReactNode {
  if (alertas.length === 0) return <span className="suave pequena">Sin avisos</span>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {alertas.map((a, i) => <li key={i}><span aria-hidden="true">⚠ </span>{a.mensaje}</li>)}
    </ul>
  );
}

function PreviewBulk({
  preview, operacion, tituloRef, quitarDeSeleccion, bloqueado,
}: {
  preview: ResultadoPreviewBulk;
  operacion: OperacionBulkPlanificacion;
  tituloRef: RefObject<HTMLHeadingElement | null>;
  quitarDeSeleccion: (idOt: string) => void;
  bloqueado: boolean;
}): ReactNode {
  const ne = preview.noElegibles;
  return (
    <section className="sep" aria-labelledby="bulk-preview-titulo">
      <h3 id="bulk-preview-titulo" ref={tituloRef} tabIndex={-1}>Previsualización</h3>
      <p className="pequena" role="status">
        {preview.total} seleccionadas · {preview.aCambiar} a modificar · {preview.sinCambio} sin cambio · {ne} no elegibles · {preview.numeroAvisos} aviso(s)
      </p>
      {ne > 0 && (
        <p className="nota error" role="alert">
          {ne === 1 ? 'Hay 1 OT que no puede modificarse. Retírala de la selección para continuar.' : `Hay ${String(ne)} OTs que no pueden modificarse. Retíralas de la selección para continuar.`}
        </p>
      )}
      {ne === 0 && preview.aCambiar === 0 && (
        <p className="nota aviso" role="status">Ninguna OT cambiaría con esta operación.</p>
      )}
      <div className="tabla-desliza">
        <table>
          <caption className="sr-solo">Resultado previsto por OT</caption>
          <thead>
            <tr>
              <th scope="col">OT</th>
              <th scope="col">Resultado</th>
              <th scope="col">{operacion === 'DESPLAZAR' ? 'Inicio actual → propuesto' : 'Asignación actual → cambio'}</th>
              <th scope="col">Avisos</th>
            </tr>
          </thead>
          <tbody>
            {preview.filas.map((f) => (
              <tr key={f.idOt}>
                <td className="mono">{f.codigo}<div className="pequena suave">{etiquetaEstadoOt(f.estado)}</div></td>
                <td>
                  <span className={`etiqueta ${claseResultado(f)}`}>{textoResultado(f, operacion)}</span>
                  {f.motivos.length > 0 && <div className="pequena">{f.motivos.join(' ')}</div>}
                  {f.apto === false && <div className="pequena"><span aria-hidden="true">⚠ </span>No apta</div>}
                  {f.resultado === 'NO_ELEGIBLE' && (
                    <button type="button" className="enlace pequena" disabled={bloqueado} onClick={() => quitarDeSeleccion(f.idOt)}>
                      Quitar {f.codigo} de la selección
                    </button>
                  )}
                </td>
                <td className="pequena">
                  {operacion === 'DESPLAZAR'
                    ? <>{formatoLocal(f.inicioActualLocal)} → {f.inicioPropuestoLocal !== null ? formatoLocal(f.inicioPropuestoLocal) : '—'} <span className="suave">({f.duracionPlanificadaMin} min)</span></>
                    : <>{f.operariosActuales.length === 0 ? 'Sin asignar' : f.operariosActuales.map((o) => o.nombre).join(', ')} → {f.cambioPropuesto}</>}
                </td>
                <td className="pequena"><ListaAvisos alertas={f.alertas} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.conflictos.length > 0 && (
        <div className="nota aviso" style={{ marginTop: 8 }}>
          <p>Conflictos detectados:</p>
          <ListaAvisos alertas={preview.conflictos} />
        </div>
      )}
    </section>
  );
}
